import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useRealtime } from '@/hooks/useRealtime';

interface Domain {
  id: string;
  name: string;
  description: string;
  icon: string | null;
  color: string | null;
  status: boolean;
  sort_order: number;
}

export default function TracksSection() {
  const navigate = useNavigate();
  const [domains, setDomains] = useState<Domain[]>([]);
  const [loading, setLoading] = useState(true);

  const loadDomains = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('domains')
        .select('*')
        .eq('status', true)
        .order('sort_order', { ascending: true })
        .order('name', { ascending: true });

      if (error) throw error;
      setDomains(data || []);
    } catch {
      // silently fail, show empty state
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadDomains();
  }, [loadDomains]);

  useRealtime<Domain>({
    table: 'domains',
    onAny: () => loadDomains(),
  });

  const handleDomainClick = (domainId: string) => {
    navigate(`/problem-statements/${domainId}`);
  };

  if (loading) {
    return (
      <section id="tracks" className="py-16 md:py-24 bg-background-50">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="text-center mb-12 md:mb-16">
            <span className="inline-block px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-semibold tracking-wide uppercase mb-3">
              Problem Tracks
            </span>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground-950">
              Choose Your <span className="text-accent-600">Challenge</span>
            </h2>
          </div>
          <div className="flex justify-center py-12">
            <div className="w-10 h-10 rounded-full border-2 border-accent-500 border-t-transparent animate-spin"></div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="tracks" className="py-16 md:py-24 bg-background-50">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="text-center mb-12 md:mb-16">
          <span className="inline-block px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-semibold tracking-wide uppercase mb-3">
            Problem Tracks
          </span>
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground-950">
            Choose Your <span className="text-accent-600">Challenge</span>
          </h2>
          <p className="text-foreground-500 mt-3 max-w-xl mx-auto text-sm">
            {domains.length > 0
              ? `${domains.length} curated problem tracks spanning the most critical areas in healthcare technology`
              : 'Problem tracks will be announced soon'}
          </p>
        </div>

        {domains.length === 0 ? (
          <div className="py-12 text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-background-100 flex items-center justify-center">
              <i className="ri-lightbulb-line text-2xl text-foreground-300"></i>
            </div>
            <p className="text-foreground-400 text-sm">No problem tracks available yet</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {domains.map((domain) => (
              <div
                key={domain.id}
                onClick={() => handleDomainClick(domain.id)}
                className="group bg-background-50 border border-background-200/70 rounded-2xl p-6 hover:border-accent-200 transition-all duration-300 cursor-pointer"
              >
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 ${
                  domain.color === 'primary' ? 'bg-primary-100' : domain.color === 'accent' ? 'bg-accent-100' : 'bg-secondary-100'
                }`}>
                  <i className={`${domain.icon || 'ri-lightbulb-line'} text-xl ${
                    domain.color === 'primary' ? 'text-primary-600' : domain.color === 'accent' ? 'text-accent-600' : 'text-secondary-600'
                  }`}></i>
                </div>
                <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-2">{domain.name}</h3>
                <p className="text-sm text-foreground-500 leading-relaxed">{domain.description || 'No description available'}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}